import { useState, useEffect, useCallback, useMemo, Fragment } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { eventsApi, phasesApi, speakersApi, camerasApi, microphonesApi, commentsApi, screensApi } from '../lib/api';
import { useSocket } from '../hooks/useSocket';
import { formatTime, calculateTotalDuration } from '../lib/utils';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { ArrowLeft, Sun, Moon, MessageSquare } from 'lucide-react';
import ClientPhaseRow from '../components/ClientPhaseRow';
import CommentsPanel from '../components/CommentsPanel';

const SECTIONS = [
  { id: 'pre-show', label: 'Pre-show' },
  { id: 'show', label: 'Show' },
  { id: 'post-show', label: 'Post-show' },
];

const buildGridTemplate = (screens = []) => [
  '1fr',
  '160px',
  '100px',
  '80px',
  ...screens.map(() => '90px'),
  '70px',
  '44px',
].join(' ');

export default function ClientViewPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { user } = useAuth();

  const [event, setEvent] = useState(null);
  const [phases, setPhases] = useState([]);
  const [speakers, setSpeakers] = useState([]);
  const [microphones, setMicrophones] = useState([]);
  const [screens, setScreens] = useState([]);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [phaseElapsed, setPhaseElapsed] = useState(0);
  const [showComments, setShowComments] = useState(false);
  const [highlightPhaseId, setHighlightPhaseId] = useState(null);

  // Socket.IO: live updates (read-only)
  const socketHandlers = useMemo(() => ({
    onEventUpdated: (data) => {
      setEvent(data);
    },
    onPhaseCreated: (phase) => {
      setPhases(prev => [...prev, phase].sort((a, b) => a.order - b.order));
    },
    onPhaseUpdated: (phase) => {
      setPhases(prev => prev.map(p => p.id === phase.id ? phase : p));
    },
    onPhaseDeleted: ({ id }) => {
      setPhases(prev => prev.filter(p => p.id !== id));
    },
    onPhasesReordered: (newPhases) => {
      setPhases(newPhases);
    },
    onTimerAction: ({ event: ev, phases: ph }) => {
      setEvent(ev);
      setPhases(ph);
    },
    onCommentCreated: (comment) => {
      setComments(prev =>
        prev.some(c => c.id === String(comment.id)) ? prev : [...prev, comment]
      );
    },
    onCommentDeleted: ({ id }) => {
      setComments(prev => prev.filter(c => c.id !== String(id)));
    },
    onCommentUpdated: (comment) => {
      setComments(prev => prev.map(c => c.id === String(comment.id) ? comment : c));
    },
    onScreensUpdated: () => {
      screensApi.getAll(eventId).then(res => setScreens(res.data)).catch(() => {});
    },
    onSpeakerCreated: (speaker) => {
      setSpeakers(prev => [...prev, speaker]);
    },
    onSpeakerUpdated: (speaker) => {
      setSpeakers(prev => prev.map(s => s.id === speaker.id ? speaker : s));
    },
    onSpeakerDeleted: ({ id }) => {
      setSpeakers(prev => prev.filter(s => s.id !== id));
    },
  }), [eventId]);

  useSocket(eventId, socketHandlers);

  // Poll comments every 5 seconds so phase badges update even with panel closed
  useEffect(() => {
    if (!eventId) return;
    const interval = setInterval(async () => {
      try {
        const res = await commentsApi.getAll(eventId);
        setComments(res.data);
      } catch (_) {}
    }, 5000);
    return () => clearInterval(interval);
  }, [eventId]);

  // Timer display (local, syncs from socket)
  useEffect(() => {
    let interval;
    if (event?.timer_running) {
      interval = setInterval(() => {
        setPhaseElapsed(prev => prev + 1);
      }, 1000);
    } else {
      setPhaseElapsed(0);
    }
    return () => clearInterval(interval);
  }, [event?.timer_running, event?.current_phase_id]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [eventRes, phasesRes, speakersRes, micsRes, screensRes, commentsRes] = await Promise.all([
          eventsApi.getOne(eventId),
          phasesApi.getAll(eventId),
          speakersApi.getAll(eventId),
          microphonesApi.getAll(eventId),
          screensApi.getAll(eventId),
          commentsApi.getAll(eventId),
        ]);
        setEvent(eventRes.data);
        setPhases(phasesRes.data);
        setSpeakers(speakersRes.data);
        setMicrophones(micsRes.data);
        setScreens(screensRes.data);
        setComments(commentsRes.data);
      } catch (error) {
        toast.error('Error al cargar el evento');
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [eventId, navigate]);

  const totalDuration = calculateTotalDuration(phases);
  const currentPhase = phases.find(p => p.id === event?.current_phase_id);
  const gridTemplate = buildGridTemplate(screens);

  const handleCommentClick = (phaseId) => {
    setHighlightPhaseId(phaseId);
    setShowComments(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="border-b border-border bg-background/95 backdrop-blur sticky top-0 z-50">
        <div className="px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="font-heading text-lg font-bold">{event?.name}</h1>
                <span className="text-xs bg-blue-500/10 text-blue-400 px-2 py-0.5 rounded font-medium">Vista cliente</span>
              </div>
              <p className="text-xs text-muted-foreground">{phases.length} fases · {formatTime(totalDuration)}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Live status */}
            {event?.timer_running && (
              <div className="flex items-center gap-2 bg-red-500/10 text-red-400 px-3 py-1.5 rounded-lg">
                <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                <span className="text-sm font-medium">En vivo</span>
                {currentPhase && (
                  <span className="text-xs text-red-300">· {currentPhase.name}</span>
                )}
              </div>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowComments(p => !p)}
              title="Comentarios"
              className="relative"
            >
              <MessageSquare className="w-4 h-4" />
              {comments.length > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-primary text-[9px] flex items-center justify-center text-primary-foreground font-bold">
                  {comments.length > 9 ? '9+' : comments.length}
                </span>
              )}
            </Button>
            <Button variant="ghost" size="icon" onClick={toggleTheme}>
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        {/* Main rundown table */}
        <div className="flex-1 overflow-auto">
          {/* Column headers */}
          <div className="sticky top-0 bg-background/95 backdrop-blur border-b border-border z-10">
            <div
              className="grid gap-2 px-3 py-2 text-xs text-muted-foreground uppercase tracking-wider font-medium"
              style={{ gridTemplateColumns: gridTemplate }}
            >
              <div className="pl-3">Fase</div>
              <div>Ponente</div>
              <div>Duración</div>
              <div>Sección</div>
              {screens.map(screen => (
                <div key={screen.id} className="truncate">{screen.name}</div>
              ))}
              <div>Estado</div>
              <div className="text-center">Notas</div>
            </div>
          </div>

          <div className="pb-20">
            {SECTIONS.map(section => {
              const sectionPhases = phases.filter(p => p.section === section.id);
              if (sectionPhases.length === 0) return null;
              return (
                <div key={section.id}>
                  <div className="sticky top-[41px] bg-muted/50 backdrop-blur px-4 py-1.5 border-b border-border z-[9]">
                    <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                      {section.label}
                    </span>
                    <span className="text-xs text-muted-foreground ml-3">
                      {formatTime(calculateTotalDuration(sectionPhases))}
                    </span>
                  </div>
                  {(() => {
                    const rootSectionPhases = sectionPhases.filter(p => !p.parent_id);
                    const subPhaseMap = phases.reduce((acc, p) => {
                      if (p.parent_id) (acc[p.parent_id] = acc[p.parent_id] || []).push(p);
                      return acc;
                    }, {});
                    const activeIdx = phases.findIndex(p => p.id === event?.current_phase_id);
                    return rootSectionPhases.map((phase, index) => {
                      const isActive = phase.id === event?.current_phase_id;
                      const phaseIdx = phases.findIndex(p => p.id === phase.id);
                      const isNext = phaseIdx === activeIdx + 1;
                      const phaseCommentCount = comments.filter(c => c.phase_id === phase.id).length;
                      return (
                        <Fragment key={phase.id}>
                          <ClientPhaseRow
                            phase={phase}
                            index={index}
                            isActive={isActive}
                            isNext={isNext}
                            phaseElapsed={isActive ? phaseElapsed : 0}
                            speakers={speakers}
                            microphones={microphones}
                            screens={screens}
                            gridTemplate={gridTemplate}
                            commentCount={phaseCommentCount}
                            onCommentClick={handleCommentClick}
                          />
                          {(subPhaseMap[phase.id] || []).map(sub => {
                            const subIsActive = sub.id === event?.current_phase_id;
                            const subCommentCount = comments.filter(c => c.phase_id === sub.id).length;
                            return (
                              <ClientPhaseRow
                                key={sub.id}
                                phase={sub}
                                isSubPhase={true}
                                isActive={subIsActive}
                                isNext={false}
                                phaseElapsed={subIsActive ? phaseElapsed : 0}
                                speakers={speakers}
                                microphones={microphones}
                                screens={screens}
                                gridTemplate={gridTemplate}
                                commentCount={subCommentCount}
                                onCommentClick={handleCommentClick}
                              />
                            );
                          })}
                        </Fragment>
                      );
                    });
                  })()}
                </div>
              );
            })}
          </div>
        </div>

        {/* Comments panel (sidebar) */}
        {showComments && (
          <div className="w-80 flex-shrink-0 border-l border-border">
            <CommentsPanel
              eventId={eventId}
              phases={phases}
              comments={comments}
              onCommentsChange={setComments}
              onClose={() => setShowComments(false)}
              highlightPhaseId={highlightPhaseId}
              isOrganizer={false}
            />
          </div>
        )}
      </div>
    </div>
  );
}
